// User Service - Handles user creation and management through Web Gateway
import { tokenService } from './tokenService';
import type { AdminUser, AdminUserCreateRequest, AdminUserUpdateRequest } from '../types/user.types';
import type { UserRole } from '../types/auth.types';
import { GATEWAY_BASE_URL } from '../config/gateway';

// Use Web Gateway for all API calls - the gateway routes to admin-user-service
const GATEWAY_URL = `${GATEWAY_BASE_URL}`;

interface ServiceResponse<T> {
  ok: boolean;
  status: number;
  data: T;
}

interface ErrorResponse {
  success: boolean;
  error: string;
  message: string;
}

interface UsersListResponse {
  success: boolean;
  data: AdminUser[];
  summary?: {
    users_with_institutions: number;
    users_without_institutions: number;
  };
}

interface RawUserListResponse {
  users?: Array<Record<string, unknown>>;
  total?: number;
  limit?: number;
  offset?: number;
}

/**
 * Backend profile returned by GET /api/v1/admin-users/me.
 * This is the authoritative source of user identity after login.
 */
export interface MeResponse {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  display_name: string;
  role: UserRole;
  permissions: string[];
  institutions: string[];
  institutionAssignments: MeInstitutionAssignment[];
  is_active: boolean;
}

export interface MeInstitutionAssignment {
  id: string;
  institution_id: string;
  role: UserRole;
  permissions: string[];
  created_at: string;
}

const VALID_USER_ROLES: UserRole[] = [
  'platform-admin',
  'platform-support',
  'org-admin',
  'org-manager',
  'org-viewer',
];

const isUserRole = (value: unknown): value is UserRole =>
  typeof value === 'string' && VALID_USER_ROLES.includes(value as UserRole);

const uniqueStrings = (values: string[]): string[] => Array.from(new Set(values));

const buildJsonHeaders = (
  token: string | null,
  institutionId?: string,
): Record<string, string> => {
  const resolvedInstitutionId = institutionId ?? tokenService.getInstitutionId();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (resolvedInstitutionId) {
    headers['X-Institution-ID'] = resolvedInstitutionId;
  }

  return headers;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizeCreateUserResponse = (payload: unknown): Record<string, unknown> => {
  const record = isRecord(payload) ? payload : {};
  const user = isRecord(record.user) ? record.user : undefined;
  const legacyData = isRecord(record.data) ? record.data : undefined;

  const createdUserId =
    typeof user?.id === 'string' && user.id.length > 0
      ? user.id
      : typeof legacyData?.id === 'string' && legacyData.id.length > 0
        ? legacyData.id
        : undefined;

  return {
    ...record,
    createdUserId,
    ...(createdUserId ? { data: { ...(legacyData ?? {}), id: createdUserId } } : {}),
  };
};

const normalizeErrorResponse = (payload: unknown, fallbackMessage: string): ErrorResponse => {
  if (!isRecord(payload)) {
    return {
      success: false,
      error: 'Request Error',
      message: fallbackMessage,
    };
  }

  const nestedError = isRecord(payload.error) ? payload.error : undefined;
  const message =
    typeof nestedError?.message === 'string'
      ? nestedError.message
      : typeof payload.message === 'string'
        ? payload.message
        : fallbackMessage;

  return {
    success: typeof payload.success === 'boolean' ? payload.success : false,
    error:
      typeof nestedError?.code === 'string'
        ? nestedError.code
        : typeof payload.error === 'string'
          ? payload.error
          : 'Request Error',
    message,
  };
};

const normalizePermissionList = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return uniqueStrings(value.filter((item): item is string => typeof item === 'string' && item.length > 0));
};

const normalizeInstitutionAssignments = (value: unknown): MeInstitutionAssignment[] => {
  if (value === undefined) {
    return [];
  }

  if (!Array.isArray(value)) {
    throw new Error('Expected institutions to be an array');
  }

  return value.map((assignment, index) => {
    if (!assignment || typeof assignment !== 'object') {
      throw new Error(`Invalid institution assignment at index ${index}`);
    }

    const record = assignment as Record<string, unknown>;
    const institutionId = record.institution_id;
    const role = record.role;

    if (typeof institutionId !== 'string' || institutionId.length === 0) {
      throw new Error(`Institution assignment ${index} is missing institution_id`);
    }

    if (!isUserRole(role)) {
      throw new Error(`Institution assignment ${index} has invalid role`);
    }

    return {
      id: typeof record.id === 'string' ? record.id : '',
      institution_id: institutionId,
      role,
      permissions: normalizePermissionList(record.permissions),
      created_at: typeof record.created_at === 'string' ? record.created_at : '',
    };
  });
};

export const resolveMePermissions = (
  _role: UserRole,
  directPermissions: string[],
  _institutionAssignments: MeInstitutionAssignment[],
): string[] => {
  return uniqueStrings(directPermissions);
};

const normalizeMeResponse = (payload: unknown): MeResponse => {
  if (!payload || typeof payload !== 'object') {
    throw new Error('Expected user profile object');
  }

  const record = payload as Record<string, unknown>;
  const id = record.id;
  const email = record.email;
  const role = record.role;
  const isActive = record.is_active;

  if (typeof id !== 'string' || id.length === 0) {
    throw new Error('User profile is missing id');
  }

  if (typeof email !== 'string' || email.length === 0) {
    throw new Error('User profile is missing email');
  }

  if (!isUserRole(role)) {
    throw new Error('User profile has invalid role');
  }

  if (typeof isActive !== 'boolean') {
    throw new Error('User profile is missing is_active');
  }

  const institutionAssignments = normalizeInstitutionAssignments(record.institutions);
  const permissions = resolveMePermissions(
    role,
    normalizePermissionList(record.permissions),
    institutionAssignments,
  );

  return {
    id,
    email,
    first_name: typeof record.first_name === 'string' ? record.first_name : '',
    last_name: typeof record.last_name === 'string' ? record.last_name : '',
    display_name: typeof record.display_name === 'string' ? record.display_name : '',
    role,
    permissions,
    institutions: uniqueStrings(
      institutionAssignments.map((assignment) => assignment.institution_id),
    ),
    institutionAssignments,
    is_active: isActive,
  };
};

const normalizeUserListItem = (value: unknown): Record<string, unknown> => {
  const record = value && typeof value === 'object' ? value as Record<string, unknown> : {};

  return {
    ...record,
    has_institutions: typeof record.has_institutions === 'boolean' ? record.has_institutions : false,
    institution_count: typeof record.institution_count === 'number' ? record.institution_count : 0,
  };
};

const normalizeUserListResponse = (payload: unknown) => {
  if (!payload || typeof payload !== 'object') {
    throw new Error('Expected user list response object');
  }

  const record = payload as RawUserListResponse;
  const users = Array.isArray(record.users) ? record.users.map(normalizeUserListItem) : [];
  const total = typeof record.total === 'number' ? record.total : users.length;
  const limit = typeof record.limit === 'number' ? record.limit : users.length;
  const offset = typeof record.offset === 'number' ? record.offset : 0;

  return {
    data: users,
    count: total,
    pagination: {
      total,
      limit,
      offset,
      currentPage: limit > 0 ? Math.floor(offset / limit) + 1 : 1,
    },
  };
};

interface UserRoleStatistics {
  platformAdmins: number;
  platformSupport: number;
  orgAdmins: number;
  orgManagers: number;
  orgViewers: number;
  totalActive: number;
  totalInactive: number;
  usersWithInstitutions: number;
  usersWithoutInstitutions: number;
}

interface PlatformUserStatsResponse {
  platform_admins?: number;
  platform_support?: number;
  org_admins?: number;
  org_managers?: number;
  org_viewers?: number;
  total_active?: number;
  total_inactive?: number;
  users_with_institutions?: number;
  users_without_institutions?: number;
}

interface GetUsersParams {
  page?: number;
  limit?: number;
  includeInactive?: boolean;
  search?: string;
  role?: string;
}

export const userService = {
  /**
   * Get the current user's profile, permissions, and institutions
   * from the backend. This is the single source of truth after login.
   */
  getMe: async (): Promise<ServiceResponse<MeResponse | ErrorResponse>> => {
    try {
      const token = await tokenService.getAccessToken();
      if (!token) {
        return {
          ok: false,
          status: 401,
          data: {
            success: false,
            error: 'Not authenticated',
            message: 'Access token unavailable',
          },
        };
      }

      const response = await fetch(`${GATEWAY_URL}/api/v1/admin-users/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          error: `HTTP ${response.status}`,
          message: response.statusText,
        }));

        return {
          ok: false,
          status: response.status,
          data: {
            success: false,
            error: errorData.error || 'Unknown error',
            message: errorData.message || 'Failed to fetch user profile',
          },
        };
      }

      const result = await response.json();
      const payload = result.data || result;

      try {
        const normalizedProfile = normalizeMeResponse(payload);

        return {
          ok: true,
          status: 200,
          data: normalizedProfile,
        };
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown normalization error';
        console.error('Invalid admin /me response:', message, payload);

        return {
          ok: false,
          status: 502,
          data: {
            success: false,
            error: 'Invalid Response',
            message: `Failed to normalize user profile: ${message}`,
          },
        };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      return {
        ok: false,
        status: 500,
        data: {
          success: false,
          error: 'Network Error',
          message: `Failed to fetch user profile: ${errorMessage}`,
        },
      };
    }
  },

  /**
   * Create institution admin user
   */
  createInstitutionAdmin: async (adminData: AdminUserCreateRequest): Promise<ServiceResponse<any>> => {
    try {
      const token = await tokenService.getAccessToken();
      const response = await fetch(`${GATEWAY_URL}/api/v1/admin-users`, {
        method: 'POST',
        headers: buildJsonHeaders(token, adminData.institutionId),
        body: JSON.stringify(adminData)
      });

      const data = await response.json();

      return {
        ok: response.ok,
        status: response.status,
        data
      };
    } catch (error) {
      console.error('Error creating institution admin:', error);
      return {
        ok: false,
        status: 500,
        data: {
          success: false,
          error: 'Network Error',
          message: 'Failed to connect to the user service'
        }
      };
    }
  },

  /**
   * Get all users for a specific institution
   */
  getInstitutionUsers: async (institutionId: string, params?: {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
    includeInactive?: boolean;
  }): Promise<ServiceResponse<UsersListResponse | ErrorResponse>> => {
    try {
      const token = await tokenService.getAccessToken();

      // Build query string with all parameters
      const queryParams = new URLSearchParams();
      queryParams.append('institutionId', institutionId);
      const limit = params?.limit ?? 20;
      const offset = params?.page ? (params.page - 1) * limit : 0;
      queryParams.append('limit', limit.toString());
      queryParams.append('offset', offset.toString());
      if (params?.search) queryParams.append('search', params.search);
      if (params?.role) queryParams.append('role', params.role);
      if (params?.includeInactive === false) queryParams.append('isActive', 'true');

      const url = `${GATEWAY_URL}/api/v1/admin-users?${queryParams.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: buildJsonHeaders(token, institutionId),
      });

      // Handle different response types
      let data: any;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        // If not JSON, try to read as text
        await response.text();
        console.error('Non-JSON response from user service');
        data = {
          success: false,
          error: 'Invalid Response',
          message: 'Server returned an invalid response format'
        };
      }

      const normalized = response.ok ? normalizeUserListResponse(data) : data;

      return {
        ok: response.ok,
        status: response.status,
        data: normalized
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      return {
        ok: false,
        status: 500,
        data: {
          success: false,
          error: 'Network Error',
          message: `Failed to connect to the user service: ${errorMessage}`
        }
      };
    }
  },

  /**
   * Create a new user for an institution, or assign an existing user by email.
   */
  createInstitutionUser: async (userData: AdminUserCreateRequest): Promise<ServiceResponse<any>> => {
    try {
      const token = await tokenService.getAccessToken();
      const response = await fetch(`${GATEWAY_URL}/api/v1/admin-users`, {
        method: 'POST',
        headers: buildJsonHeaders(token, userData.institutionId),
        body: JSON.stringify(userData)
      });

      const data = await response.json();

      return {
        ok: response.ok,
        status: response.status,
        data: response.ok
          ? normalizeCreateUserResponse(data)
          : normalizeErrorResponse(data, 'Failed to create user')
      };
    } catch (error) {
      console.error('Error creating institution user:', error);
      return {
        ok: false,
        status: 500,
        data: {
          error: 'Network Error',
          message: 'Failed to connect to the user service'
        }
      };
    }
  },

  /**
   * Update a user
   */
  updateUser: async (userId: string, updates: AdminUserUpdateRequest, institutionId?: string): Promise<ServiceResponse<any>> => {
    try {
      const token = await tokenService.getAccessToken();
      const response = await fetch(`${GATEWAY_URL}/api/v1/admin-users/${userId}`, {
        method: 'PUT',
        headers: buildJsonHeaders(token, institutionId),
        body: JSON.stringify(updates)
      });

      const data = await response.json();

      return {
        ok: response.ok,
        status: response.status,
        data
      };
    } catch (error) {
      console.error('Error updating user:', error);
      return {
        ok: false,
        status: 500,
        data: {
          error: 'Network Error',
          message: 'Failed to connect to the user service'
        }
      };
    }
  },

  /**
   * Delete (deactivate) a user
   */
  deleteUser: async (userId: string, institutionId?: string): Promise<ServiceResponse<any>> => {
    try {
      const token = await tokenService.getAccessToken();
      const response = await fetch(`${GATEWAY_URL}/api/v1/admin-users/${userId}`, {
        method: 'DELETE',
        headers: buildJsonHeaders(token, institutionId),
      });

      const data = await response.json();

      return {
        ok: response.ok,
        status: response.status,
        data
      };
    } catch (error) {
      console.error('Error deleting user:', error);
      return {
        ok: false,
        status: 500,
        data: {
          error: 'Network Error',
          message: 'Failed to connect to the user service'
        }
      };
    }
  },

  /**
   * Get platform-wide user role statistics for the platform dashboard card.
   */
  getPlatformUserStatistics: async (): Promise<ServiceResponse<UserRoleStatistics | ErrorResponse>> => {
    try {
      const token = await tokenService.getAccessToken();
      const response = await fetch(`${GATEWAY_URL}/api/v1/admin-users/platform/stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json() as PlatformUserStatsResponse;

      const roleStats: UserRoleStatistics = {
        platformAdmins: result.platform_admins ?? 0,
        platformSupport: result.platform_support ?? 0,
        orgAdmins: result.org_admins ?? 0,
        orgManagers: result.org_managers ?? 0,
        orgViewers: result.org_viewers ?? 0,
        totalActive: result.total_active ?? 0,
        totalInactive: result.total_inactive ?? 0,
        usersWithInstitutions: result.users_with_institutions ?? 0,
        usersWithoutInstitutions: result.users_without_institutions ?? 0,
      };

      return {
        ok: true,
        status: 200,
        data: roleStats
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      return {
        ok: false,
        status: 500,
        data: {
          success: false,
          error: 'Network Error',
          message: `Failed to fetch user statistics: ${errorMessage}`
        }
      };
    }
  },

  getUserRoleStatistics: async (): Promise<ServiceResponse<UserRoleStatistics | ErrorResponse>> =>
    userService.getPlatformUserStatistics(),

  /**
   * Get user role statistics for a specific institution
   */
  getInstitutionUserStatistics: async (institutionId: string): Promise<ServiceResponse<UserRoleStatistics | ErrorResponse>> => {
    try {
      const token = await tokenService.getAccessToken();

      // Fetch users for the specific institution
      const response = await fetch(`${GATEWAY_URL}/api/v1/admin-users?institutionId=${institutionId}&limit=1000`, {
        method: 'GET',
        headers: buildJsonHeaders(token, institutionId),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const users: AdminUser[] = result.data || result.users || [];
      const active = users.filter(u => (u.is_active ?? true));
      const inactive = users.filter(u => !(u.is_active ?? true));

      const roleStats: UserRoleStatistics = {
        platformAdmins: 0,
        platformSupport: 0,
        orgAdmins: active.filter(u => u.role === 'org-admin').length,
        orgManagers: active.filter(u => u.role === 'org-manager').length,
        orgViewers: active.filter(u => u.role === 'org-viewer').length,
        totalActive: active.length,
        totalInactive: inactive.length,
        usersWithInstitutions: users.length,
        usersWithoutInstitutions: 0,
      };

      return {
        ok: true,
        status: 200,
        data: roleStats
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      return {
        ok: false,
        status: 500,
        data: {
          success: false,
          error: 'Network Error',
          message: `Failed to fetch institution user statistics: ${errorMessage}`
        }
      };
    }
  },

  /**
   * Get all users with pagination and filters
   */
  getAllUsers: async (params: GetUsersParams = {}): Promise<ServiceResponse<any>> => {
    try {
      const token = await tokenService.getAccessToken();
      const limit = params.limit || 20;
      const offset = ((params.page || 1) - 1) * limit;
      const queryParams = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
        ...(params.search && { search: params.search }),
        ...(params.role && !params.role.includes(',') && { role: params.role })
      });
      if (params.includeInactive === false) {
        queryParams.append('isActive', 'true');
      }

      const response = await fetch(`${GATEWAY_URL}/api/v1/admin-users?${queryParams}`, {
        method: 'GET',
        headers: buildJsonHeaders(token),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const normalized = normalizeUserListResponse(result);

      return {
        ok: true,
        status: 200,
        data: normalized
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      return {
        ok: false,
        status: 500,
        data: {
          error: 'Network Error',
          message: `Failed to fetch users: ${errorMessage}`
        }
      };
    }
  },

  /**
   * Get a single user by ID
   */
  getUserById: async (userId: string, institutionId?: string): Promise<ServiceResponse<AdminUser | ErrorResponse>> => {
    try {
      const token = await tokenService.getAccessToken();
      const response = await fetch(`${GATEWAY_URL}/api/v1/admin-users/${userId}`, {
        method: 'GET',
        headers: buildJsonHeaders(token, institutionId),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      return {
        ok: true,
        status: 200,
        data: result.data || result
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      return {
        ok: false,
        status: 500,
        data: {
          success: false,
          error: 'Network Error',
          message: `Failed to fetch user: ${errorMessage}`
        }
      };
    }
  },

  getUserInstitutions: async (userId: string, institutionId?: string): Promise<ServiceResponse<Array<{ id: string; institution_id: string; name?: string; role: string }> | ErrorResponse>> => {
    try {
      const token = await tokenService.getAccessToken();
      const response = await fetch(`${GATEWAY_URL}/api/v1/admin-users/${userId}/institutions`, {
        method: 'GET',
        headers: buildJsonHeaders(token, institutionId),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const assignments = Array.isArray(result) ? result : (result.data || []);
      const institutions = assignments.map((a: { id: string; institution_id: string; institution_name?: string; role: string; name?: string }) => ({
        id: a.institution_id || a.id,
        institution_id: a.institution_id || a.id,
        name: a.institution_name || a.name || a.institution_id || a.id,
        role: a.role,
      }));

      return { ok: true, status: 200, data: institutions };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      return {
        ok: false,
        status: 500,
        data: {
          success: false,
          error: 'Network Error',
          message: `Failed to fetch user institutions: ${errorMessage}`
        }
      };
    }
  },
};
